import React, { useEffect, useState } from 'react';
import { Breadcrumbs, FileUploader, Input, Multiselect } from '@cokitchen/cokitchen-components';
import PageHeaderText from 'components/base/page-header-text';
import useEditPostContext from 'contexts/edit-post.context';
import { ROUTE_KEYS, STORAGE_KEYS } from 'utils/constants';
import { Editor } from 'react-draft-wysiwyg';
import './styles.scss';
import { ContentState, convertFromHTML, convertToRaw, EditorState } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import PageLoader from 'components/base/page-loader';
import _ from 'lodash';
import { generateKey } from 'utils/functions';
import SelectDropdown from 'components/base/select-dropdown';
import { ENUM_POST_TYPES } from 'utils/enums';

const EditPostDetails = () => {
  const [textEditorState, setTextEditorState] = useState<EditorState>();
  const {
    editPostForm,
    setEditPostForm,
    setDisabled,
    postToEdit,
    postLoading,
    initialEditData,
    polygons,
    meals,
    brands
  } = useEditPostContext();

  const [selectedOption, setSelectedOption] = useState<string | undefined>(undefined);

  const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN) as string;

  const breadcrumbs = [
    { name: 'Manage homepage', path: ROUTE_KEYS.HOME },
    { name: 'Edit post', path: ROUTE_KEYS.EDIT_POST }
  ];

  useEffect(() => {
    if (postToEdit) {
      setSelectedOption(postToEdit.post_type);
    }

    if (postToEdit) {
      let body;
      try {
        body = convertFromHTML(JSON.parse(postToEdit.body)).contentBlocks;
      } catch (error) {
        return;
      }
      setTextEditorState(EditorState.createWithContent(ContentState.createFromBlockArray(body)));
    }
  }, [postToEdit]);

  const handleEditorStateChange = (e: EditorState) => {
    setTextEditorState(e);
    setEditPostForm({
      ...editPostForm,
      body: JSON.stringify(draftToHtml(convertToRaw(e.getCurrentContent())))
    });
  };

  useEffect(() => {
    if (!editPostForm.heading || !editPostForm.title || !editPostForm.body || !editPostForm.image) {
      setDisabled(true);
    } else {
      const hasChanged = !_.isEqual(initialEditData, editPostForm);
      if (hasChanged) {
        setDisabled(false);
      } else {
        setDisabled(true);
      }
    }
  }, [editPostForm]);

  const brandOptions = brands.map((b) => ({
    text: b.name,
    value: b.id
  }));

  const mealOptions = meals.map((m) => ({
    text: m.name,
    value: m.id
  }));

  const postOptions = [
    { id: ENUM_POST_TYPES.FCB, name: 'Fcb' },
    { id: ENUM_POST_TYPES.FC, name: 'Fc' },
    { id: ENUM_POST_TYPES.BUDGET, name: 'Budget' },
    { id: ENUM_POST_TYPES.SHOP, name: 'Shop' }
  ];

  if (postLoading || !postToEdit) return <PageLoader />;
  return (
    <div className='mx-10 pt-8 max-w-[700px]'>
      <Breadcrumbs breadcrumbs={breadcrumbs} />
      <div className='mt-8'>
        <PageHeaderText title='Edit Post' description='Edit a post lorem dolum ipsum' />
      </div>
      <Input
        type='text'
        label='Post heading'
        name='heading'
        value={editPostForm.heading}
        onChange={(e) =>
          setEditPostForm({
            ...editPostForm,
            heading: e.target.value
          })
        }
        className='mb-6'
        placeholder='Post heading'
        inputClass='h-[50px]'
      />
      <Input
        type='text'
        label='Post title'
        name='title'
        value={editPostForm.title}
        onChange={(e) =>
          setEditPostForm({
            ...editPostForm,
            title: e.target.value
          })
        }
        className='mb-6'
        placeholder='Eat Good, Pay Less.'
        inputClass='h-[50px]'
      />
      <p className='text-black mb-1.5'>Post body</p>
      <Editor
        editorState={textEditorState}
        toolbarClassName='toolbarClassName'
        wrapperClassName='wrapperClassName'
        editorClassName='editorClassName'
        onEditorStateChange={handleEditorStateChange}
        placeholder="You Know how you buy a really awesome gift for someone and you're just so excited out of your shoes for the person to experience it? 🤭 Think that, think Red Pepper! 🌶️📸
with ❤️"
      />
      <p className='font-medium mt-5'>Add post image</p>
      {editPostForm?.image?.length && (
        <FileUploader
          className='p-0 border-0'
          key={editPostForm.image.length}
          token={token}
          setFileUrls={(urls) => {
            setEditPostForm({
              ...editPostForm,
              image: urls
            });
          }}
          fileUrls={editPostForm.image}
        />
      )}
      <div className='mt-4 mb-8'>
        <label className='font-medium'>Location</label>
        <Multiselect
          placeholder='Select Location'
          values={editPostForm.location || []}
          options={polygons.map((p) => ({
            text: p.name,
            value: p.id
          }))}
          onChange={(locations) =>
            setEditPostForm({
              ...editPostForm,
              location: locations
            })
          }
        />
      </div>
      <div className='mb-8'>
        <label className='font-medium'>Brands</label>
        <Multiselect
          isSingle
          key={generateKey(brandOptions)}
          placeholder='Select brand'
          values={editPostForm.brand_id ? [editPostForm.brand_id] : []}
          options={brandOptions}
          onChange={(val) =>
            setEditPostForm({
              ...editPostForm,
              brand_id: val?.[0],
              meal_id: ''
            })
          }
          hasSearch
          filterResults
        />
      </div>
      <div className='mb-8'>
        <label className='font-medium'>Meals</label>
        <Multiselect
          isSingle
          key={generateKey(mealOptions)}
          placeholder='Select meal'
          values={editPostForm.meal_id ? [editPostForm.meal_id] : []}
          options={mealOptions}
          onChange={(val) =>
            setEditPostForm({
              ...editPostForm,
              meal_id: val?.[0]
            })
          }
          hasSearch
          filterResults
        />
      </div>
      <div className='mb-8'>
        <SelectDropdown
          label='Choose a Post Type'
          placeholder='Select post type'
          options={postOptions}
          value={selectedOption}
          setValue={(option) => {
            setSelectedOption(option.id);
            setEditPostForm({
              ...editPostForm,
              post_type: option.id
            });
          }}
        />
      </div>
    </div>
  );
};

export default EditPostDetails;
